import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#1a1a2e",
          color: "#fff8e7",
          fontFamily: '"VT323", monospace',
          padding: 24,
        }}
        data-testid="error-boundary-fallback"
      >
        <div style={{ maxWidth: 520, textAlign: "center" }}>
          <h1 style={{ color: "#ffb420", fontSize: 28, marginBottom: 12 }}>Something broke.</h1>
          <p style={{ fontSize: 18, marginBottom: 20, color: "#c8a96e" }}>
            The game hit an unexpected error and stopped. Your progress for the current round may be
            lost. Reload to start fresh.
          </p>
          {this.state.error?.message && (
            <pre
              style={{
                background: "#2d1b0e",
                color: "#ef5350",
                padding: 12,
                borderRadius: 4,
                fontSize: 13,
                textAlign: "left",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                marginBottom: 20,
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              background: "#ffb420",
              color: "#2d1b0e",
              border: "none",
              padding: "12px 32px",
              fontFamily: '"VT323", monospace',
              fontSize: 20,
              cursor: "pointer",
              letterSpacing: 2,
            }}
            data-testid="error-boundary-reload"
          >
            ▶ RELOAD
          </button>
        </div>
      </div>
    );
  }
}
